<div class="page-shell api-settings">
  <mat-toolbar class="header">
    <span>{{ "restApi.title" | translate }}</span>
    <div class="actions">
      <button
        mat-stroked-button
        type="button"
        (click)="downloadDocumentationJson()"
      >
        <mat-icon>download</mat-icon>
        {{ "restApi.downloadDocJson" | translate }}
      </button>
      <button
        mat-raised-button
        color="primary"
        type="button"
        (click)="openCreateTokenDialog()"
      >
        <mat-icon>add</mat-icon>
        {{ "settings.api.createToken" | translate }}
      </button>
    </div>
  </mat-toolbar>

  <div class="api-page">
    <section class="api-panel" id="api-keys">
      <div
        class="api-panel__body api-panel__body--table table-container"
        [class.table-container--loading]="isLoading"
        [class.table-container--empty]="!isLoading && tokens.length === 0"
      >
        <table
          mat-table
          [dataSource]="dataSource"
          class="api-tokens-table mat-elevation-z0"
        >
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>
              {{ "settings.api.tokenName" | translate }}
            </th>
            <td mat-cell *matCellDef="let token">{{ token.name }}</td>
          </ng-container>

          <ng-container matColumnDef="keyPrefix">
            <th mat-header-cell *matHeaderCellDef>
              {{ "settings.api.keyPrefix" | translate }}
            </th>
            <td mat-cell *matCellDef="let token">
              <span class="key-prefix">{{ token.keyPrefix }}...</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="lastUsedAt">
            <th mat-header-cell *matHeaderCellDef>
              {{ "settings.api.lastUsedAt" | translate }}
            </th>
            <td mat-cell *matCellDef="let token">
              {{
                token.lastUsedAt
                  ? (token.lastUsedAt | localizedDate: "medium")
                  : ("settings.api.neverUsed" | translate)
              }}
            </td>
          </ng-container>

          <ng-container matColumnDef="expiresAt">
            <th mat-header-cell *matHeaderCellDef>
              {{ "settings.api.expiresAt" | translate }}
            </th>
            <td mat-cell *matCellDef="let token">
              {{
                token.expiresAt
                  ? (token.expiresAt | localizedDate: "medium")
                  : ("settings.api.neverExpires" | translate)
              }}
            </td>
          </ng-container>

          <ng-container matColumnDef="createdAt">
            <th mat-header-cell *matHeaderCellDef>
              {{ "settings.api.createdAt" | translate }}
            </th>
            <td mat-cell *matCellDef="let token">
              {{ token.createdAt | localizedDate: "medium" }}
            </td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>
              {{ "settings.api.status" | translate }}
            </th>
            <td mat-cell *matCellDef="let token">
              <span [ngClass]="tokenStatusClass(token)">
                {{ tokenStatusLabel(token) | translate }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let token">
              <div class="table-actions">
                <button
                  type="button"
                  class="action-btn action-btn--danger"
                  *ngIf="token.active && !token.isExpired"
                  (click)="revokeToken(token.keyId)"
                  [matTooltip]="'settings.api.revokeTooltip' | translate"
                  [attr.aria-label]="'settings.api.revokeTooltip' | translate"
                >
                  <mat-icon>block</mat-icon>
                </button>
                <button
                  type="button"
                  class="action-btn action-btn--danger"
                  (click)="deleteToken(token.keyId)"
                  [matTooltip]="'settings.api.deleteTooltip' | translate"
                  [attr.aria-label]="'settings.api.deleteTooltip' | translate"
                >
                  <mat-icon>delete_outline</mat-icon>
                </button>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>

        <div class="loading-wrapper" *ngIf="isLoading">
          <app-loading></app-loading>
        </div>

        <div class="empty-state" *ngIf="!isLoading && tokens.length === 0">
          <mat-icon class="empty-state__icon">vpn_key</mat-icon>
          <p>{{ "settings.api.noTokens" | translate }}</p>
        </div>
      </div>
    </section>

    <div class="api-docs">
      <aside class="api-panel api-docs__nav">
        <div class="api-panel__header">
          <h2 class="api-panel__title">{{ "restApi.documentation" | translate }}</h2>
        </div>
        <div class="api-panel__body api-docs__nav-body">
          <div class="base-url-box">
            <div class="base-url-box__label">{{ "restApi.baseUrl" | translate }}</div>
            <div class="base-url-box__row">
              <code class="base-url-box__value">{{ baseUrl }}</code>
              <button
                mat-icon-button
                type="button"
                (click)="copyBaseUrl()"
                [matTooltip]="
                  (baseUrlCopied ? 'restApi.copied' : 'restApi.copy') | translate
                "
              >
                <mat-icon>{{ baseUrlCopied ? "check" : "content_copy" }}</mat-icon>
              </button>
            </div>
            <p class="base-url-box__hint">{{ "restApi.useBaseUrl" | translate }}</p>
          </div>

          <div class="api-nav">
            <div class="nav-group" *ngFor="let section of sections">
              <button
                type="button"
                class="nav-group__header"
                (click)="toggleSection(section.id)"
              >
                <span>{{ section.labelKey | translate }}</span>
                <mat-icon>{{
                  isSectionOpen(section.id) ? "expand_less" : "expand_more"
                }}</mat-icon>
              </button>
              <ng-container *ngIf="isSectionOpen(section.id)">
                <button
                  type="button"
                  class="api-nav-item"
                  *ngFor="let endpoint of section.endpoints"
                  [class.api-nav-item--selected]="
                    selectedSectionId === section.id &&
                    selectedEndpointId === endpoint.id
                  "
                  (click)="selectEndpoint(section.id, endpoint.id)"
                >
                  <span
                    class="method-tag"
                    [ngClass]="methodClass(endpoint.method)"
                    >{{ endpoint.method }}</span
                  >
                  <span class="api-nav-item__label">{{
                    (endpoint.labelKey || endpoint.titleKey) | translate
                  }}</span>
                </button>
              </ng-container>
            </div>
          </div>
        </div>
      </aside>

      <section
        class="api-panel api-docs__main"
        *ngIf="selectedEndpoint as selected; else restApiWelcome"
      >
        <div class="api-panel__header api-docs__main-header">
          <div class="api-docs__title-row">
            <span
              class="method-tag method-tag--lg"
              [ngClass]="methodClass(selected.endpoint.method)"
              >{{ selected.endpoint.method }}</span
            >
            <h2 class="api-panel__title">
              {{ selected.endpoint.titleKey | translate }}
            </h2>
          </div>
          <code class="endpoint-path">{{ selected.endpoint.path }}</code>
        </div>

        <div class="api-panel__body api-docs__content">
          <div class="info-block">
            <h3>{{ "restApi.authentication" | translate }}</h3>
            <p>{{ "restApi.allApiRequests" | translate }}</p>
            <p>{{ "restApi.replaceApiKey" | translate }}</p>
          </div>

          <p class="endpoint-description">
            {{ selected.endpoint.descriptionKey | translate }}
          </p>

          <ng-container *ngIf="selected.endpoint.pathParams?.length">
            <h3 class="block-title">{{ "restApi.pathParameters" | translate }}</h3>
            <div class="params-table">
              <div
                class="params-row"
                *ngFor="let param of selected.endpoint.pathParams; trackBy: trackParam"
              >
                <div class="params-row__name">
                  <code>{{ param.name }}</code>
                  <span class="required-tag" *ngIf="param.required !== false">{{
                    "restApi.requiredTag" | translate
                  }}</span>
                </div>
                <div class="params-row__meta">
                  <strong>{{ param.typeKey | translate }}</strong>
                  <span>{{ param.descriptionKey | translate }}</span>
                </div>
              </div>
            </div>
          </ng-container>

          <ng-container *ngIf="selected.endpoint.queryParams?.length">
            <h3 class="block-title">{{ "restApi.queryParameters" | translate }}</h3>
            <div class="params-table">
              <div
                class="params-row"
                *ngFor="let param of selected.endpoint.queryParams; trackBy: trackParam"
              >
                <div class="params-row__name">
                  <code>{{ param.name }}</code>
                  <span class="required-tag" *ngIf="param.required">{{
                    "restApi.requiredTag" | translate
                  }}</span>
                  <span class="optional-tag" *ngIf="!param.required">{{
                    "restApi.optionalTag" | translate
                  }}</span>
                </div>
                <div class="params-row__meta">
                  <strong>{{ param.typeKey | translate }}</strong>
                  <span>{{ param.descriptionKey | translate }}</span>
                </div>
              </div>
            </div>
          </ng-container>

          <ng-container *ngIf="selected.endpoint.bodyParams?.length">
            <h3 class="block-title">{{ "restApi.requestBody" | translate }}</h3>
            <div class="params-table">
              <ng-container
                *ngFor="let param of selected.endpoint.bodyParams; trackBy: trackParam"
              >
                <ng-container
                  *ngTemplateOutlet="bodyParamRow; context: { $implicit: param, depth: 0 }"
                ></ng-container>
              </ng-container>
            </div>
          </ng-container>

          <ng-template #bodyParamRow let-param let-depth="depth">
            <div
              class="params-row"
              [class.params-row--nested]="depth > 0"
              [class.params-row--has-nested]="
                !!param.properties?.length || !!param.oneOf?.length
              "
            >
              <div class="params-row__name">
                <code>{{ param.name }}</code>
                <span class="required-tag" *ngIf="param.required">{{
                  "restApi.requiredTag" | translate
                }}</span>
                <span class="optional-tag" *ngIf="!param.required">{{
                  "restApi.optionalTag" | translate
                }}</span>
              </div>
              <div class="params-row__meta">
                <strong>{{ param.typeKey | translate }}</strong>
                <span>{{ param.descriptionKey | translate }}</span>
              </div>

              <div class="params-nested" *ngIf="param.oneOf?.length">
                <div class="params-nested__label">
                  {{ "restApi.oneOfVariants" | translate }}
                </div>
                <div
                  class="params-oneof"
                  *ngFor="let variant of param.oneOf; trackBy: trackParam"
                >
                  <div class="params-oneof__head">
                    <code>{{ variant.name }}</code>
                    <strong>{{ variant.typeKey | translate }}</strong>
                  </div>
                  <span class="params-oneof__desc">{{
                    variant.descriptionKey | translate
                  }}</span>
                  <div class="params-nested" *ngIf="variant.properties?.length">
                    <div class="params-nested__label">
                      {{ "restApi.objectProperties" | translate }}
                    </div>
                    <div class="params-table params-table--nested">
                      <ng-container
                        *ngFor="let child of variant.properties; trackBy: trackParam"
                      >
                        <ng-container
                          *ngTemplateOutlet="
                            bodyParamRow;
                            context: { $implicit: child, depth: depth + 1 }
                          "
                        ></ng-container>
                      </ng-container>
                    </div>
                  </div>
                </div>
              </div>

              <div class="params-nested" *ngIf="param.properties?.length">
                <div class="params-nested__label">
                  {{ "restApi.objectProperties" | translate }}
                </div>
                <div class="params-table params-table--nested">
                  <ng-container
                    *ngFor="let child of param.properties; trackBy: trackParam"
                  >
                    <ng-container
                      *ngTemplateOutlet="
                        bodyParamRow;
                        context: { $implicit: child, depth: depth + 1 }
                      "
                    ></ng-container>
                  </ng-container>
                </div>
              </div>
            </div>
          </ng-template>

          <div class="code-block-header">
            <h3 class="block-title">{{ "restApi.request" | translate }}</h3>
            <button
              mat-icon-button
              type="button"
              class="code-block-header__copy"
              (click)="copyText(replacePlaceholders(selected.endpoint.requestExample))"
              [matTooltip]="'restApi.copy' | translate"
            >
              <mat-icon>content_copy</mat-icon>
            </button>
          </div>
          <div class="code-block">
            <pre><code [innerHTML]="highlightCode(replacePlaceholders(selected.endpoint.requestExample))"></code></pre>
          </div>

          <div class="code-block-header">
            <h3 class="block-title">{{ "restApi.response" | translate }}</h3>
            <button
              mat-icon-button
              type="button"
              class="code-block-header__copy"
              (click)="
                copyText(formatSample(selected.endpoint.responseSamples[selectedResponseCode]))
              "
              [matTooltip]="'restApi.copy' | translate"
            >
              <mat-icon>content_copy</mat-icon>
            </button>
          </div>
          <div class="response-tabs">
            <button
              type="button"
              class="response-tab"
              *ngFor="let code of responseCodes"
              [class.is-active]="selectedResponseCode === code"
              [ngClass]="statusClass(code)"
              (click)="selectedResponseCode = code"
            >
              {{ code }}
            </button>
          </div>
          <p class="response-label">
            {{ statusLabelKey(selectedResponseCode) | translate }}
          </p>
          <div class="code-block">
            <pre><code [innerHTML]="highlightCode(formatSample(selected.endpoint.responseSamples[selectedResponseCode]))"></code></pre>
          </div>
        </div>
      </section>

      <ng-template #restApiWelcome>
        <section class="api-panel api-docs__main">
          <div class="api-panel__header">
            <h2 class="api-panel__title">{{ "restApi.welcomeTitle" | translate }}</h2>
          </div>
          <div class="api-panel__body api-docs__content">
            <p class="endpoint-description">
              {{ "restApi.welcomeIntro" | translate }}
            </p>

            <div class="info-block">
              <h3>{{ "restApi.welcomeStep1Title" | translate }}</h3>
              <p>
                {{ "restApi.welcomeStep1Description" | translate }}
                <a href="#api-keys">{{ "restApi.apiKeysSection" | translate }}</a>.
              </p>
            </div>

            <div class="info-block">
              <h3>{{ "restApi.welcomeStep2Title" | translate }}</h3>
              <p>{{ "restApi.welcomeStep2Description" | translate }}</p>
              <div class="code-block">
                <pre><code>Authorization: Bearer YOUR_API_KEY</code></pre>
              </div>
            </div>

            <div class="info-block">
              <h3>{{ "restApi.welcomeStep3Title" | translate }}</h3>
              <p>{{ "restApi.welcomeStep3Description" | translate }}</p>
            </div>

            <p class="endpoint-description">
              {{ "restApi.welcomeCta" | translate }}
            </p>
          </div>
        </section>
      </ng-template>
    </div>
  </div>
</div>
